/**
 * TitleScreen - The Garden Entrance
 *
 * The first scene children see when opening the app.
 * Not a "menu" but an inviting garden space that naturally
 * leads into exploration.
 *
 * Design principles:
 * - World IS the interface (no buttons)
 * - Everything is touchable and alive
 * - Transitions naturally to first zone
 */

import { Application, Container, Sprite, Assets, Graphics } from 'pixi.js';
import { getAudioManager, getSoundPlayer } from '@game/audio';

export class TitleScreen {
  private app: Application;
  private container: Container;
  private musicPlaying = false;
  private audioUnlocked = false;
  private onEnterGarden?: () => void;

  // Store elements for resize handling
  private sky: Sprite | null = null;
  private ground: Sprite | null = null;
  private foliage: Sprite | null = null;
  private titleSign: Sprite | null = null;
  private butterfly: Sprite | null = null;
  private plantLeft: Sprite | null = null;
  private settingsIcon: Container | null = null;
  private butterflyFloatCallback: (() => void) | null = null;

  constructor(app: Application, onEnterGarden?: () => void) {
    this.app = app;
    this.container = new Container();
    this.onEnterGarden = onEnterGarden;
  }

  /**
   * Load all title screen assets
   */
  async load(): Promise<void> {
    // Load visual assets only (audio loaded via SoundLoader separately)
    await Assets.load([
      '/assets/title-scene/sky-background.png',
      '/assets/title-scene/ground-layer.png',
      '/assets/title-scene/foliage-canopy.png',
      '/assets/title-scene/title-sign.png',
      '/assets/title-scene/butterfly.png',
      '/assets/title-scene/plant-left.png'
    ]);

    console.log('✓ Title screen assets loaded');
  }

  /**
   * Create and display the title screen
   */
  async show(): Promise<void> {
    this.container.removeChildren();

    this.layoutScene();

    // Add to stage
    this.app.stage.addChild(this.container);

    // Make scene interactive IMMEDIATELY (before music)
    this.setupInteractions();

    // Try to start music (don't await - let it happen in background)
    this.tryStartMusic().catch(err => {
      console.log('Music start failed (expected on iOS):', err);
    });

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Layout all scene elements (called initially and on resize)
   */
  private layoutScene(): void {
    const screenWidth = this.app.screen.width;
    const screenHeight = this.app.screen.height;

    // Layer 1: Sky background
    if (!this.sky) {
      this.sky = Sprite.from('/assets/title-scene/sky-background.png');
      this.container.addChild(this.sky);
    }
    this.sky.width = screenWidth;
    this.sky.height = screenHeight;

    // Layer 2: Ground (bottom of screen)
    if (!this.ground) {
      this.ground = Sprite.from('/assets/title-scene/ground-layer.png');
      this.container.addChild(this.ground);
    }
    this.ground.width = screenWidth;
    this.ground.height = screenHeight * 0.5;
    this.ground.y = screenHeight * 0.5;

    // Layer 3: Foliage canopy (top of screen)
    if (!this.foliage) {
      this.foliage = Sprite.from('/assets/title-scene/foliage-canopy.png');
      this.container.addChild(this.foliage);
    }
    this.foliage.width = screenWidth;
    this.foliage.height = screenHeight * 0.4;
    this.foliage.y = 0;

    // Layer 4: Plant on left side
    if (!this.plantLeft) {
      this.plantLeft = Sprite.from('/assets/title-scene/plant-left.png');
      this.container.addChild(this.plantLeft);
    }
    this.plantLeft.height = screenHeight * 0.6;
    this.plantLeft.width = this.plantLeft.height * (this.plantLeft.texture.width / this.plantLeft.texture.height);
    this.plantLeft.x = 20;
    this.plantLeft.y = screenHeight - this.plantLeft.height - 50;

    // Layer 5: Title sign (center)
    if (!this.titleSign) {
      this.titleSign = Sprite.from('/assets/title-scene/title-sign.png');
      this.container.addChild(this.titleSign);
    }
    this.titleSign.width = screenWidth * 0.6;
    this.titleSign.height = this.titleSign.width * (this.titleSign.texture.height / this.titleSign.texture.width);
    this.titleSign.x = screenWidth / 2 - this.titleSign.width / 2;
    this.titleSign.y = screenHeight / 2 - this.titleSign.height / 2 - 50;

    // Layer 6: Butterfly (scaled based on screen width)
    if (!this.butterfly) {
      this.butterfly = Sprite.from('/assets/title-scene/butterfly.png');
      this.butterfly.texture.source.scaleMode = 'linear';
      this.butterfly.eventMode = 'static';
      this.butterfly.cursor = 'pointer';
      this.container.addChild(this.butterfly);

      // Start animation on first creation and store callback
      this.butterflyFloatCallback = this.animateFloat(this.butterfly, 0);
    }

    // Scale butterfly based on screen size (larger on wider screens)
    const butterflySize = Math.min(screenWidth * 0.25, 400);
    this.butterfly.width = butterflySize;
    this.butterfly.height = butterflySize;
    this.butterfly.x = screenWidth * 0.7;
    this.butterfly.y = screenHeight * 0.5; // Middle - matches transition end

    // Layer 7: Settings icon (bottom right, for parents)
    if (!this.settingsIcon) {
      this.settingsIcon = this.createSettingsIcon();
      this.container.addChild(this.settingsIcon);
    }
    this.settingsIcon.x = screenWidth - 60;
    this.settingsIcon.y = screenHeight - 60;
  }

  /**
   * Handle window resize
   */
  private handleResize(): void {
    this.layoutScene();
    // Update hit area for interactions
    this.container.hitArea = this.app.screen;
  }

  /**
   * Try to start music immediately
   * Will fail on iOS (needs user gesture), but works on other platforms
   */
  private async tryStartMusic(): Promise<void> {
    const audioManager = getAudioManager();

    try {
      // Attempt to unlock audio immediately
      const unlocked = await audioManager.unlock();

      if (unlocked) {
        this.audioUnlocked = true;
        console.log('✓ Audio unlocked automatically');
        await this.playBackgroundMusic();
      }
    } catch (err) {
      // Expected on iOS - will unlock on first tap
      console.log('⏸ Audio needs user gesture (iOS) - will unlock on first tap');
      this.audioUnlocked = false;
      this.musicPlaying = false;
    }
  }

  /**
   * Create settings icon
   */
  private createSettingsIcon(): Container {
    const settingsContainer = new Container();

    // Wooden circular backing
    const backing = new Graphics();
    backing.circle(0, 0, 35);
    backing.fill({ color: 0xA67C52 }); // Warm wood color

    // Darker border
    backing.circle(0, 0, 35);
    backing.stroke({ color: 0x8B6F47, width: 3 });

    settingsContainer.addChild(backing);

    // Gear icon (simple, stylized)
    const gear = new Graphics();

    // Center circle
    gear.circle(0, 0, 12);
    gear.fill({ color: 0x5A8C69 }); // Green like plants

    // Gear teeth (6 teeth)
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI * 2) / 6;
      const x1 = Math.cos(angle) * 12;
      const y1 = Math.sin(angle) * 12;

      gear.rect(x1 - 3, y1 - 3, 6, 14);
      gear.fill({ color: 0x5A8C69 });
    }

    // Inner circle (hole)
    gear.circle(0, 0, 5);
    gear.fill({ color: 0x4A7C59 });

    settingsContainer.addChild(gear);

    // Make interactive
    settingsContainer.eventMode = 'static';
    settingsContainer.cursor = 'pointer';

    // Hover effect
    settingsContainer.on('pointerover', () => {
      settingsContainer.scale.set(1.1);
    });

    settingsContainer.on('pointerout', () => {
      settingsContainer.scale.set(1.0);
    });

    // Click handler
    settingsContainer.on('pointerdown', (event) => {
      event.stopPropagation(); // Don't trigger title screen tap
      this.openSettings();
    });

    return settingsContainer;
  }

  /**
   * Open settings overlay (for voice selection)
   */
  private openSettings(): void {
    console.log('⚙️ Settings tapped - would open voice selector');
    // TODO: Create settings overlay
    
    // For now, just log
  }

  /**
   * Gentle floating animation for creatures
   */
  private animateFloat(sprite: Sprite, delay: number): (() => void) {
    const startY = sprite.y;
    const amplitude = 15;
    const speed = 0.001;

    const animateCallback = () => {
      const time = Date.now() * speed;
      sprite.y = startY + Math.sin(time) * amplitude;
    };

    setTimeout(() => {
      this.app.ticker.add(animateCallback);
    }, delay);

    return animateCallback;
  }

  /**
   * Play background music
   */
  private async playBackgroundMusic(): Promise<void> {
    if (this.musicPlaying) {
      console.log('Music already playing');
      return;
    }

    console.log('Attempting to play background music...');

    const player = getSoundPlayer();

    try {
      await player.play('/sounds/ambient/title-theme.mp3', {
        channel: 'background',
        volume: 0.25, // Increased volume
        loop: true,
        autoDuck: false // Background music should not duck itself
      });

      this.musicPlaying = true;
      console.log('✓ Background music playing (looped, volume: 0.6)');
    } catch (err) {
      console.error('❌ Background music failed to play:', err);
      this.musicPlaying = false;
    }
  }

  /**
   * Setup interactions - first tap unlocks audio, subsequent taps progress
   */
  private setupInteractions(): void {
    console.log('🎮 Setting up title screen interactions...');

    // Make entire scene tappable
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    this.container.interactiveChildren = false; // Scene itself is clickable, not children

    // Set explicit hit area covering entire screen
    const screenWidth = this.app.screen.width;
    const screenHeight = this.app.screen.height;

    this.container.hitArea = {
      contains: (x: number, y: number) => {
        return x >= 0 && x <= screenWidth && y >= 0 && y <= screenHeight;
      }
    };

    console.log(`📍 Hit area set: ${screenWidth}x${screenHeight}`);

    this.container.on('pointerdown', async () => {
      console.log('🖱️ Title screen tapped!', { unlocked: this.audioUnlocked, musicPlaying: this.musicPlaying });

      // Ensure audio is unlocked on first tap
      if (!this.audioUnlocked) {
        console.log('🎵 First tap - unlocking audio...');
        const audioManager = getAudioManager();
        const unlocked = await audioManager.unlock();

        if (unlocked) {
          this.audioUnlocked = true;
          console.log('✓ Audio unlocked');
        } else {
          console.error('❌ Failed to unlock audio');
          return;
        }
      }

      // Always try to start music if not playing
      if (!this.musicPlaying) {
        console.log('🎵 Starting background music...');
        await this.playBackgroundMusic();
      } else {
        // Music is playing, enter the garden
        console.log('🌱 Entering garden...');
        if (this.onEnterGarden) {
          this.onEnterGarden();
        }
      }
    });
  }

  /**
   * Animate transition out with fade and Flutter flight
   * Returns Flutter's final position for seamless handoff
   */
  async transitionOut(): Promise<{ x: number; y: number }> {
    // Stop the floating animation so transition has full control
    if (this.butterflyFloatCallback) {
      this.app.ticker.remove(this.butterflyFloatCallback);
      this.butterflyFloatCallback = null;
    }

    return new Promise((resolve) => {
      // Get Flutter's current position
      if (!this.butterfly) {
        resolve({ x: 0, y: 0 });
        return;
      }

      const startX = this.butterfly.x;
      const startY = this.butterfly.y; // Capture current Y (includes float offset)
      const endX = this.app.screen.width * 0.5; // Middle of screen
      const endY = this.app.screen.height * 0.5; // Target Y position
      const duration = 1500;
      const startTime = Date.now();

      // Transition animation
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic for smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 3);

        // Fade out all elements EXCEPT Flutter (she stays visible)
        if (this.sky) this.sky.alpha = 1 - progress * 0.8;
        if (this.ground) this.ground.alpha = 1 - progress * 0.8;
        if (this.foliage) this.foliage.alpha = 1 - progress * 0.8;
        if (this.titleSign) this.titleSign.alpha = 1 - progress;
        if (this.plantLeft) this.plantLeft.alpha = 1 - progress * 0.9;
        if (this.settingsIcon) this.settingsIcon.alpha = 1 - progress;

        // Flutter stays at full opacity - no fade
        // Fly her to target position smoothly
        if (this.butterfly) {
          this.butterfly.x = startX + (endX - startX) * eased;
          this.butterfly.y = startY + (endY - startY) * eased;
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Return final position for seamless handoff
          resolve({ x: endX, y: endY });
        }
      };

      animate();
    });
  }

  /**
   * Clean up and remove title screen
   */
  hide(): void {
    this.app.stage.removeChild(this.container);
    // Music will continue playing (background for whole game)
  }
}
